import { useState } from "react"
import styled from "styled-components"

import {
  colors,
  RadioButton,
  Skeleton,
  spacing,
} from "@minuthq/meatball-ui-react"

import { TContextTarget } from "src/components/AiAssistant/aiAssistantTypes"
import { SearchFilter } from "src/components/Filter/SearchFilter"
import { Pager } from "src/components/Pager/Pager"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomes } from "src/data/organizations/queries/homeQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { MDialog } from "src/ui/Dialog/MDialog"

import { HomeGroupFilter } from "./HomeGroupFilter"

const LIMIT = 10

export function ContextTargetSelectDialog({
  onClose,
  onSelect,
}: {
  onClose: () => void
  onSelect: (target: TContextTarget) => void
}) {
  const { tPlaceholder } = useTranslate()
  const { orgId } = useOrganization()

  // Used for filtering
  const [searchText, setSearchText] = useState("")
  const [selectedHomeGroup, setSelectedHomeGroup] = useState<{
    homeGroupId: string
    homeGroupName: string
  } | null>(null)
  const [offset, setOffset] = useState(0)

  const [selectedTarget, setSelectedTarget] = useState<TContextTarget | null>(
    null
  )

  const fetchHomes = useFetchHomes({
    orgId,
    filters: {
      name: searchText || undefined,
      homegroup_id: selectedHomeGroup
        ? selectedHomeGroup.homeGroupId
        : undefined,
      limit: LIMIT,
      offset,
    },
  })

  function handleSelect() {
    if (!selectedTarget) {
      return
    }

    onSelect(selectedTarget)
  }

  function handleTargetSelect(target: TContextTarget) {
    setSelectedTarget(target)
  }

  function renderResults() {
    if (fetchHomes.isLoading) {
      return (
        <div>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={48} width="100%" />
          ))}
        </div>
      )
    }

    if (fetchHomes.data?.homes.length === 0) {
      return <div>{tPlaceholder("No units found")}</div>
    }

    const hasFilteredByHomeGroup = selectedHomeGroup !== null

    return (
      <ResultsWrapper>
        <RadioButtonsWrapper>
          {hasFilteredByHomeGroup && (
            <RadioButton
              key={selectedHomeGroup.homeGroupId}
              name="target-selection"
              label={tPlaceholder(
                `All units in ${selectedHomeGroup.homeGroupName}`
              )}
              value={selectedHomeGroup.homeGroupId}
              onSelected={() =>
                handleTargetSelect({
                  type: "homegroup",
                  id: selectedHomeGroup.homeGroupId,
                  name: selectedHomeGroup.homeGroupName,
                })
              }
              selected={
                selectedTarget?.type === "homegroup" &&
                selectedTarget.id === selectedHomeGroup.homeGroupId
              }
            />
          )}

          {fetchHomes.data?.homes.map((home) => (
            <RadioButton
              key={home.home_id}
              name="target-selection"
              label={home.name || home.home_id}
              value={home.home_id}
              onSelected={() =>
                handleTargetSelect({
                  type: "home",
                  id: home.home_id,
                  name: home.name || home.home_id,
                })
              }
              selected={
                selectedTarget?.type === "home" &&
                selectedTarget.id === home.home_id
              }
            />
          ))}
        </RadioButtonsWrapper>

        <div onClick={(e) => e.preventDefault()}>
          <Pager
            limit={LIMIT}
            offset={offset}
            setOffset={setOffset}
            totalCount={fetchHomes.data?.paging.total_count}
          />
        </div>
      </ResultsWrapper>
    )
  }

  return (
    <MDialog
      open={true}
      onClose={onClose}
      title={tPlaceholder("Add unit")}
      description={tPlaceholder(
        "Select the unit, building, or location this content should apply to."
      )}
      confirmLabel={tPlaceholder("Add unit")}
      confirmButtonProps={{
        disabled: !selectedTarget,
      }}
      onConfirm={handleSelect}
    >
      <Container>
        <FilterWrapper>
          <SearchFilterWrapper>
            <SearchFilter
              onChange={setSearchText}
              placeholder={tPlaceholder("Search unit")}
            />
          </SearchFilterWrapper>

          <HomeGroupFilter
            label={
              !selectedHomeGroup
                ? tPlaceholder("Filter by building/location")
                : selectedHomeGroup.homeGroupName
            }
            selectedHomeGroupId={selectedHomeGroup?.homeGroupId}
            onSelectionChange={setSelectedHomeGroup}
            placeholder={tPlaceholder("Select buildings or locations...")}
          />
        </FilterWrapper>

        {renderResults()}
      </Container>
    </MDialog>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.L};
  height: 40vh;
`

const FilterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${spacing.M};
`

const SearchFilterWrapper = styled.div`
  max-width: 250px;
`

const ResultsWrapper = styled.div`
  display: grid;
  gap: ${spacing.M};
`

const RadioButtonsWrapper = styled.div`
  display: grid;

  & > * {
    height: 48px;
  }

  & > *:not(:last-child) {
    border-bottom: 1px solid ${colors.divider};
  }
`
